import log from "../log";
import time from "../time/time";
import { config_get, config_set } from "../db/t_config";
import { msg_batch_add, msg_count, msg_query_one, msg_update_stat, Msg, RTCContent, Meta } from "../db/t_msg";
import { userobj_upsert, UserObj } from "../db/t_userobj";
import { topic_del, topic_del_all, topic_modify, topic_query_one } from "../db/t_topic";
import { user_upsert, User, user_modify } from "../db/t_user";
import Context from "./context";
import { Group, group_upsert, update_group_remark } from "../db/t_group";
import {
    group_member_missing_uids,
    group_member_qry_topic,
    group_member_remove_notin,
    group_member_update_mine,
    group_member_upsert,
    GroupMember,
} from "../db/t_group_member";
import { user_ref_unset_friend, user_ref_upsert } from "../db/t_user_ref";
import UserController from "./user";
import { GroupController } from "./group";
import { CryptEngine } from "../../src/utils/CryptEngine";
import { buildSingleTopicId, getTypeByTopicid } from "../../src/utils/helper";
import { ErrorCodePermission } from "../../src/net/http";
import { FullSyncRunner, FULL_TASK_THRESHOLD } from "./full_sync";
import { config_app_is_miso, getInstance } from ".";
import { RTCEngine } from "../../src/utils/RTCEngine";
import { MessageMaskType, MessageType } from "../../src/constants/types";
import { group_summary_del, group_summary_update } from "../db/t_group_summary";
import { TenantSettings } from "../db/t_user_devices";
import { isSystemAcc } from "../../src/screen/Chat/CListItem";

const min_timeout = 0;
const max_timeout = 30 * time.SECOND;
const default_timeout = 5 * time.SECOND;

const full_max_count = 100; // 当前的任务数积压超过或等于这个值，给一个回调通知
const full_min_count = 0; // 小于这个值就不是积压状态
const full_notify_delta = 5; // 差值超过1再通知回调，目的是防止callback太多

const STAT_PREFIX_MSG = "/msg/";
const STAT_PREFIX_USER = "/user/";
const STAT_PREFIX_USEROBJ = "/userobj/";
const STAT_PREFIX_GROUP = "/group/"; // 群状态
const STAT_PREFIX_TOPIC = "/topic/"; // 会话状态
const STAT_PREFIX_COMPANY_CONTACT = "/company-contact/"; // 企业通讯录变化了 通过状态通知到前端 前端标记后 再合适时间调/sdk/company/contact 更新企业通讯录
const STAT_PREFIX_NOTICE = "/notice/"; // 公告
const STAT_PREFIX_QUESTION_BANK = "/question-bank/"; // 问题库
const STAT_PREFIX_USEFULL_WORD = "/usefull-word/"; // 常用短语

type Stat = {
    key: string;
    uptime: number;
    data: any;
};

// 请求：上报会话的最大消息信息
type Ack = {
    tid: string; // 会话id
    mid: string; // 确认最大的消息id
};

export class SyncRunable {
    private lastSyncIds = { sid: 0, aid: 0 }; // 上一次的状态，用户判断是否需要写数据库
    private syncIds = { sid: 0, aid: 0 }; // 订阅消息和状态，是作为最后的任务处理

    private is_full: boolean; // 是否进入了积压状态，挤压状态要不断通知当前处理进度，外层好进入同步页面
    private full_last_process: number; // 上次的进度 百分制 0 - 100

    private readonly ctx: Context;

    private isStop: boolean = false; // 是否已经停止
    private isRunning: boolean = false; // 是否在启动中

    /** 全量分页同步（与增量 /sync 互斥） */
    private fullSyncRunner: FullSyncRunner;
    /** 是否已完成过全量同步（防止 sid=0 时无限重试全量） */
    private _hasFullSynced: boolean = false;

    // cache
    private next_acks: Ack[] = []; // 下一次sync的时候把这些ack带上去

    // 任务 按优先处理排序
    private task_topic_msgs: MsgLatestTopic[] = []; // 会话消息同步任务
    private task_stats: Stat[] = []; // 状态同步任务

    constructor(ctx: Context) {
        this.is_full = false;
        this.full_last_process = 0;
        this.ctx = ctx;
        this.fullSyncRunner = new FullSyncRunner(ctx);
    }

    public Run() {
        log.info("[SyncRunable] Run()", { isRunning: this.isRunning });
        if (!this.isRunning) {
            this.isRunning = true;
            this.runable()
                .then(() => {
                    log.info("sync runable 退出了");
                })
                .catch((err) => {
                    log.warn("sync runable 异常退出了", err);
                });
        }
    }

    public Destory() {
        if (!this.isStop) {
            this.isStop = true;
            this.fullSyncRunner.abort();
            log.info("Destory SyncRunable");
        }
    }

    // 计算当前任务数
    private calcTaskCount(): number {
        let count: number = 0;
        count += this.task_stats.length;
        count += this.task_topic_msgs.length;
        return count;
    }

    /** 状态去重入队：同一个 key 只保留 uptime 最大的 */
    private push_stats_dedup(newStats: Stat[]) {
        for (const s of newStats) {
            const idx = this.task_stats.findIndex((t) => t.key === s.key);
            if (idx >= 0) {
                if (s.uptime > this.task_stats[idx].uptime) {
                    this.task_stats[idx] = s;
                }
            } else {
                this.task_stats.push(s);
            }
        }
    }

    /** 消息去重入队：同一个 tid 合并消息列表（按 mid 去重） */
    private push_topic_msgs_dedup(topics: MsgLatestTopic[]) {
        for (const t of topics) {
            const idx = this.task_topic_msgs.findIndex((m) => m.tid === t.tid);
            if (idx >= 0) {
                const old = this.task_topic_msgs[idx];
                const midSet = new Set(old.msgs.map((m) => m.mid));
                for (const msg of t.msgs) {
                    if (!midSet.has(msg.mid)) {
                        old.msgs.push(msg);
                        midSet.add(msg.mid);
                    }
                }
                if (t.sid > old.sid) {
                    old.sid = t.sid;
                    old.maxMsgId = t.maxMsgId;
                    old.next = t.next || old.next;
                }
                if (t.minMsgId < old.minMsgId) {
                    old.minMsgId = t.minMsgId;
                }
                if (t.next) {
                    old.next = true;
                }
            } else {
                this.task_topic_msgs.push(t);
            }
        }
    }

    // 获取本地存储的同步id
    private async load_syncids(): Promise<{
        error: any;
        sid: number;
        aid: number;
    }> {
        let error: any = null;
        let sid: number = 0;
        let aid: number = 0;
        const sidCfg = await config_get(this.ctx.db, "sid");
        if (!sidCfg.error) {
            if (sidCfg.data) {
                sid = parseInt(sidCfg.data.val, 10);
                log.info("config_get sid = ", sid);
            } else {
                sid = 0;
                log.info("config_get sid = 0");
            }
        } else {
            error = sidCfg.error;
            log.error("config_get sid err:", sidCfg.error);
        }
        const aidCfg = await config_get(this.ctx.db, "aid");
        if (!aidCfg.error) {
            if (aidCfg.data) {
                aid = parseInt(aidCfg.data.val, 10);
                log.info("config_get aid = ", aid);
            } else {
                // 只同步三天内的状态
                aid = time.toUnixMillSecond(time.add(time.now(), -3 * time.DAY));
                // aid = 0;
                log.info("config_get first set aid = ", aid);
            }
        } else {
            error = aidCfg.error;
            log.error("config_get aid err:", aidCfg.error);
        }
        return { error, sid, aid };
    }

    // 主循环 从任务队列拿任务
    private async runable(): Promise<null> {
        log.info("[SyncRunable] runable() 开始");
        const sids = await this.load_syncids();
        if (sids.error) {
            log.error("load_syncids err:", sids.error);
            this.ctx.conn
                .Call("/report/record", {
                    load_syncids: sids.error,
                })
                .then();
            return null;
        }
        this.syncIds.aid = sids.aid;
        this.syncIds.sid = sids.sid;

        const fullSyncedCfg = await config_get(this.ctx.db, "has_full_synced");
        this._hasFullSynced = fullSyncedCfg.data?.val === "1";
        if (this._hasFullSynced) {
            log.info(
                "[Sync] 已完成过全量同步，sid=",
                this.syncIds.sid,
                "aid=",
                this.syncIds.aid,
            );
        }

        if (this.syncIds.sid > 0) {
            try {
                const cntRst = await this.ctx.db.Query(
                    "SELECT COUNT(*) as cnt FROM t_topic",
                    [],
                );
                if (cntRst.data && cntRst.data.length > 0 && cntRst.data[0].cnt === 0) {
                    log.info("[Sync] sid > 0 但本地无会话数据，重置 sid=0 强制全量同步");
                    this.syncIds.sid = 0;
                    this._hasFullSynced = false;
                    await config_set(this.ctx.db, "sid", "0");
                    await config_set(this.ctx.db, "has_full_synced", "0");
                    this.lastSyncIds.sid = 0;
                }
            } catch (e) {
                log.warn("[Sync] 检查本地会话数量异常，跳过", e);
            }
        }

        let timeout: number = 0;
        while (true) {
            if (timeout > 0) {
                await time.sleep(timeout);
            }
            try {
                timeout = await this.pop_do_task();
                this.ctx.mq.emit("sync_loop_done", {});
                if (timeout < min_timeout || timeout > max_timeout) {
                    log.warn("[KKIM] runable dotask return invalid timeout:", timeout);
                    timeout = default_timeout;
                }
            } catch (e) {
                log.error("[KKIM] runable dotask err:", e);
                timeout = 10 * time.SECOND;
            }
            if (!this.is_running()) {
                break;
            }
        }

        log.info("[Sync] runable stoped");
        return null;
    }

    private is_running(): boolean {
        return this.isRunning && !this.isStop && !this.ctx.stop;
    }

    // 当本地有很多任务的时候，通知页面弹出个覆盖层，然后有个进度条慢慢同步，直到完成
    private async check_notify_fulling(): Promise<null> {
        const task_count = this.calcTaskCount();
        if (task_count > 0) {
            log.info("剩余任务数:", task_count);
        }
        if (this.is_full) {
            // 是否完成
            const is_done = task_count <= full_min_count;
            // 任务有可能增加，但任务%只能越来越少，所以做一个修正
            const p = is_done ? 100 : Math.max(this.full_last_process, (100 * (full_max_count - task_count)) / full_max_count);
            // 进度是否超过阀值 目的：防止callback太频繁，有效进度操作一定数量再callback
            const is_up = is_done ? true : p - this.full_last_process >= full_notify_delta;

            if (is_up) {
                this.full_last_process = p;
                this.ctx.mq.emit("full_process", {
                    full_process: {
                        name: "process",
                        process: p,
                        count: task_count,
                    },
                });
            }

            if (is_done) {
                this.is_full = false;
                this.ctx.mq.emit("full_process", {
                    full_process: {
                        name: "done",
                        process: 100,
                        count: task_count,
                    },
                });
            }
        } else if (task_count >= full_max_count) {
            log.info("积压产生了!!剩余任务数:", task_count, "积压配置:", full_max_count);
            this.is_full = true;
            this.ctx.mq.emit("full_process", {
                full_process: {
                    name: "begin",
                    process: 0,
                    count: task_count,
                },
            });
        }
        return null;
    }

    // 返回下一次处理时间
    private async pop_do_task(): Promise<number> {
        await this.check_notify_fulling(); // 检查当前任务的积压情况

        // 先存消息，因为状态和消息是分开来的，如果状态先到本地没有数据
        if (this.task_topic_msgs && this.task_topic_msgs.length > 0) {
            log.info("=====>>待处理消息的会话数量", this.task_topic_msgs.length);
            await this.do_task_topic_msgs();
            return 0; // 无需等待 立即执行 todo有时候还是要等待下，防止UI卡顿
        }

        // 再处理状态
        if (this.task_stats && this.task_stats.length > 0) {
            log.info("待处理状态同步数量", this.task_stats.length);
            await this.do_task_stats();
            return 0; // 无需等待 立即执行 todo时候还是要等待下，防止UI卡顿
        }

        // .. 处理其它任务

        // 前面任务都做完成了 才能认为sync终于可以了，这里将sid和aid落库持久化，当然要判断下的确有变更再落库，减少数据库的操作频次
        // 开发阶段这段先注释掉，后期再打开
        if (this.lastSyncIds.sid !== this.syncIds.sid) {
            this.lastSyncIds.sid = this.syncIds.sid;
            await config_set(this.ctx.db, "sid", this.syncIds.sid.toString(10));
        }
        if (this.lastSyncIds.aid !== this.syncIds.aid) {
            this.lastSyncIds.aid = this.syncIds.aid;
            await config_set(this.ctx.db, "aid", this.syncIds.aid.toString(10));
        }

        // 前面会计算出变更了的 会话+消息 等 这里计算变更的内容进行通知
        return await this.do_sync();
    }

    // 处理状态同步
    private async do_task_stats(): Promise<null> {
        // 当前的同步任务扫一遍做个聚合，有些类型可以批量处理
        const t = this.task_stats.pop();
        if (!(t && t.key && t.key.length > 0)) {
            log.warn("BUG状态类型为空，可能是后台问题");
            return null;
        }
        if (t.key.startsWith(STAT_PREFIX_MSG)) {
            log.info("[状态同步]消息", t.key);
            await this.do_stat_msg(t);
        } else if (t.key.startsWith(STAT_PREFIX_USEROBJ)) {
            log.info("[状态同步]用户对象", t);
            await this.do_stat_userobj(t);
        } else if (t.key.startsWith(STAT_PREFIX_USER)) {
            log.info("[状态同步]用户", t.key);
            await this.do_stat_user(t);
        } else if (t.key.startsWith(STAT_PREFIX_COMPANY_CONTACT)) {
            log.info("[状态同步]公司通讯录", t.key);
            await this.do_contact_refresh(t);
        } else if (t.key.startsWith(STAT_PREFIX_TOPIC)) {
            log.info("[状态同步]会话");
            // XXX 这个同步不需要处理， 当初是为了䢎前端节省工作量添加，但是前端没有用
            // 现在重写数据层更加不需要用到，但这里站位 TODO 当app都上线后，后台除去该同步代码
        } else if (t.key.startsWith(STAT_PREFIX_GROUP)) {
            log.info("[状态同步]群", t.key);
            await this.do_stat_group(t);
        } else if (t.key.startsWith(STAT_PREFIX_QUESTION_BANK)) {
            log.info("[状态同步]问题库", t.key);
        } else if (t.key.startsWith(STAT_PREFIX_USEFULL_WORD)) {
            log.info("[状态同步]常用语", t.key);
        } else if (t.key.startsWith(STAT_PREFIX_NOTICE)) {
            log.info("[状态同步]通知", t.key);
        } else {
            log.warn("未知的状态类型", t.key, "详细：", t);
            console.log("========未知的状态类型", t.key);
        }

        return null;
    }

    private async do_contact_refresh(s: Stat): Promise<void> {
        this.ctx.mq.emit("contact_refresh", {});
    }

    private async do_stat_group(s: Stat): Promise<undefined> {
        if (!s.data.group) {
            return;
        }
        // 存群信息
        let group = s.data.group as Group;
        //这个同步消息内有些字段没有，导致群信息没有更新，需要重新获取一次
        const r = await GroupController.GetRemote(this.ctx, group.tid);
        if (r.group) {
            group = r.group;
        }
        //群解散就不处理了
        if (r.group?.flag === 2) {
            await group_summary_del(this.ctx.db, group.tid);
            await topic_del(this.ctx.db, group.tid);
            this.ctx.mq.emit(
                "group_delete",
                { group_delete: { tid: group.tid } },
                this.is_full,
            );
            return;
        }
        // 先修正topic的name信息
        await topic_modify(this.ctx.db, {
            tid: group.tid,
            name: group.name,
        });
        await group_upsert(this.ctx.db, group);
        if (s.data.members) {
            // todo 检查群成员本地有没有数据，没有的话进行补齐
            const members = s.data.members as number[];
            const n = members.length;
            if (n > 0) {
                const r2 = await GroupController.GetUserRemote(this.ctx, group.tid, members);
                if (r2.data && r2.data.users && r2.data.users.length > 0) {
                    const m = r2.data.users.length;
                    const users = [];
                    for (let i = 0; i < m; i++) {
                        const member = r2.data.users[i] as GroupMember;
                        member.tid = group.tid;
                        users.push(member);
                    }
                    await group_member_upsert(this.ctx.db, users);
                }
            }
        }

        // 清除不存在的记录
        await group_member_remove_notin(this.ctx.db, group.tid, s.data.members);

        await group_summary_update(this.ctx.db, group.tid, { ...s.data.group });

        /*   this.ctx.mq.emit(
              "group_update",
              {
                  group_update: { tid: group.tid }
              },
              this.is_full
          ); */
        // 更新会话 ,上面可以不用，现在都是同样的处理逻辑，没必要处理两遍
        this.ctx.mq.emit(
            "topic_update",
            {
                topic_update: { tid: group.tid },
            },
            this.is_full
        );

        return;
    }

    private async do_stat_user(s: Stat): Promise<undefined> {
        if (!(s.data && s.data.user)) {
            log.warn("BUG!!未知用户同步数据格式", JSON.stringify(s));
            return;
        }
        const d = s.data.user as User;
        const r = await user_upsert(this.ctx.db, d);
        if (r.error) {
            // todo db错误无解 做好日志监控，给用户提示db错误等待修复
            log.error("do_stat_user->user_upsert dberr:", r.error, "stat:", JSON.stringify(s));
        }
        await group_member_update_mine(this.ctx.db, d);

        // 将单聊会话的名称进行修正
        const tid = this.ctx.uid < d.uid ? `s_${this.ctx.uid}_${d.uid}` : `s_${d.uid}_${this.ctx.uid}`;
        this.ctx.mq.emit("topic_update", { topic_update: { tid: tid } }, this.is_full);

        this.ctx.mq.emit("userinfo", { userinfo: d }, this.is_full);

        // 查群聊
        const r2 = await group_member_qry_topic(this.ctx.db, d.uid);
        if (r2.error) {
            log.error("查询涉及", d.uid, "群头像会话异常", r2.error);
        }
        if (r2.tids && r2.tids.length > 0) {
            const n = r2.tids.length;
            for (let i = 0; i < n; i++) {
                this.ctx.mq.emit(
                    "topic_update",
                    {
                        topic_update: { tid: r2.tids[i] },
                    },
                    this.is_full
                );
            }
        } else {
            log.info("查询涉及", d.uid, "群头像会话为空");
        }
        return;
    }

    private async do_stat_user_device(s: Stat): Promise<undefined> {
        const r = await UserController.updateLoginDevices(this.ctx);
        if (r.error) {
            log.warn("更新登录设备列表失败", r.error);
            return
        }
        return;
    }

    // 用户对象
    private async do_stat_userobj(s: Stat): Promise<undefined> {
        if (!s.data) {
            return;
        }
        const d = s.data as UserObj;
        const r = await userobj_upsert(this.ctx.db, d);
        if (r.error) {
            log.error(`更新用户对象${d.resType}:${d.resId}失败`, r.error);
            return;
        }

        if (d.resType === "user_device") {
            log.debug("[状态同步]用户设备更新", d.resType, d.resId);
            await this.do_stat_user_device(s);
            return;
        }

        if (d.resType === "tenant_settings") {
            log.debug("[状态同步] 开关控制更新", JSON.stringify(d));
            if (!d.data) {
                log.warn("BUG!!未知开关控制更新数据格式", JSON.stringify(s));
                return;
            }

            try {
                const jsonObject = JSON.parse(d.data ?? '{}');
                const tenant_settings = { uid: this.ctx.uid, settings: jsonObject as TenantSettings }
                this.ctx.mq.emit_stash("tenant_settings", { tenant_settings });
            } catch (error) {
                console.error('Invalid JSON string:', error);
            }
            return;
        }

        if (d.resType === "friend_rel") {
            const friendUid = parseInt(d.resId, 10);
            const op = d.data; // "add" "delete"
            if (op === "add") {
                log.debug("[状态同步]添加好友", friendUid);
                const r1 = await UserController.GetMuti(this.ctx, [friendUid], true);
                if (r1.users && r1.users.length > 0) {
                    const friendPlat = r1.users[0].appkey.charAt(0);
                    const myPlat = this.ctx.appkey.charAt(0);
                    await user_ref_upsert(
                        this.ctx.db,
                        {
                            uid: r1.users[0].uid,
                            isfriend: 1, // 0 - 不是朋友 1 - 是朋友
                            isstaff: 0, // 0 - 不是同事 1 - 是同事
                            ischain: friendPlat !== myPlat ? 1 : 0, // 0 - 不是同事 1 - 是同事 是不是供应链联系人
                            plat: friendPlat, // 写上平台方便查
                            appkey: r1.users[0].appkey,
                            main_appkey: r1.users[0].mainAppkey,
                            lutime: 0,
                            lctime: 0,
                        },
                        ["friend", "chain"]
                    );
                    this.ctx.mq.emit_stash("contact_refresh", {});
                    const uid = getInstance().CurrentUid();
                    const tid = buildSingleTopicId(r1.users[0].uid, uid);
                    this.ctx.mq.emit_stash("topic_new", { topic_new: { tidList: [tid] } });
                }
            } else if (op === "delete") {
                log.debug("[状态同步]删除好友", friendUid);
                await user_ref_unset_friend(this.ctx.db, friendUid);
                this.ctx.mq.emit_stash("contact_refresh", {});
            }
        } else if (d.resType === "friend_apply") {
            if (d.resId === "new_cnt") {
                const cnt = parseInt(d.data, 10); // 新好友的请求数
                const cntStr = cnt.toString(10);
                log.info("[状态同步]新的好友请求数", d.data, cnt);
                const r2 = await config_get(this.ctx.db, "friend_apply");
                if (!(r2.data && r2.data.val && r2.data.val === cntStr)) {
                    await config_set(this.ctx.db, "friend_apply", cntStr);
                    this.ctx.mq.emit_stash("friend_apply", {
                        friend_apply: { count: cnt },
                    });
                }
            }
        } else if (d.resType === "topic_delete") {
            const tid = d.resId;
            const hidemid = d.data; // 其实不是删除 是隐藏 如果会话当前消息小于这个值则隐藏会话
            const r1 = await topic_modify(this.ctx.db, {
                tid: tid,
                hidemid: hidemid,
            });
            if (r1.error) {
                // todo 写入到重试任务 后面再回调
            }
            this.ctx.mq.emit(
                "topic_delete",
                {
                    topic_delete: { tid: tid, hidemid: hidemid },
                },
                this.is_full
            );
        } else if (d.resType === "topic_mute") {
            const tid = d.resId;
            const mute = d.data === "true"; // 是否静音
            const r1 = await topic_modify(this.ctx.db, { tid: tid, mute: mute });
            if (r1.error) {
                // todo 写入到重试任务 后面再回调
            }
            this.ctx.mq.emit(
                "topic_mute",
                {
                    topic_mute: { tid: tid, mute: mute },
                },
                this.is_full
            );
        } else if (d.resType === "topic_remark") {
            const tid = d.resId;
            const remark = d.data || ""; // 备注名
            const r1 = await topic_modify(this.ctx.db, {
                tid: tid,
                remark: remark,
            });
            await update_group_remark(this.ctx.db, tid, remark);
            await group_summary_update(this.ctx.db, tid, { remark });
            if (r1.error) {
                // todo 写入到重试任务 后面再回调
            }
            this.ctx.mq.emit(
                "topic_remark",
                {
                    topic_remark: { tid: tid, remark: remark },
                },
                this.is_full
            );
        } else if (d.resType === "topic_to_top") {
            const tid = d.resId;
            let order = 0; // 是否置顶
            if (d.data === "true") {
                order = s.uptime;
            }
            const r1 = await topic_modify(this.ctx.db, {
                tid: tid,
                order: order,
            });
            if (r1.error) {
                // todo 写入到重试任务 后面再回调
            }
            this.ctx.mq.emit(
                "topic_to_top",
                {
                    topic_to_top: { tid: tid, order: order },
                },
                this.is_full
            );
        } else if (d.resType === "topic_visit") {
            const tid = d.resId;
            const maxshowmid = d.data; // 查看到的最大的消息 用于多端同步进行记数
            const r1 = await topic_modify(this.ctx.db, {
                tid: tid,
                lsMid: maxshowmid,
                lvTime: d.utime,
                unread: 0,
            });
            if (r1.error) {
                // todo 写入到重试任务 后面再回调
            } else {
                this.ctx.mq.emit(
                    "topic_visit",
                    {
                        topic_visit: {
                            tid: tid,
                            maxshowmid: maxshowmid,
                            utime: d.utime,
                        },
                    },
                    this.is_full
                );
            }
        } else if (d.resType === "user_head") {
            // 修改用户头像
            const uid = parseInt(d.resId, 10);
            if (!isNaN(uid)) {
                const avatar = d.data || "";
                // todo 事件通知
                const r1 = await user_modify(this.ctx.db, {
                    uid: uid,
                    avatar: avatar,
                });
                if (r1.error) {
                    // todo 写入到重试任务 后面再回调
                }
                this.ctx.mq.emit(
                    "user_head",
                    {
                        user_head: { uid: uid, avatar: avatar },
                    },
                    this.is_full
                );
            } else {
                log.warn(`BUG!!用户对象user_head的resId${d.resId}解析失败`); // 因为绝不可能所以打BUG日志
            }
        } else if (d.resType === "user_remark") {
            // 修改用户备注
            const uid = parseInt(d.resId, 10);
            if (!isNaN(uid)) {
                const remark = d.data || "";
                const r1 = await user_modify(this.ctx.db, {
                    uid: uid,
                    remark: remark,
                });
                if (r1.error) {
                    // todo 写入到重试任务 后面再回调
                }
                this.ctx.mq.emit(
                    "user_remark",
                    {
                        user_remark: { uid: uid, remark: remark },
                    },
                    this.is_full
                );
            } else {
                log.warn(`BUG!!用户对象user_remark的resId${d.resId}解析失败`); // 因为绝不可能所以打BUG日志
            }
        } else if (d.resType === "group_apply") {
            this.ctx.mq.emit("group_apply", { group_apply: { tid: d.data } }, this.is_full);
        } else if (d.resType === "group_black") {
            this.ctx.mq.emit("group_black", { group_black: { tid: d.data } }, this.is_full);
        } else if (d.resType === "user_permission") {
            const { msgAssistant, msgTranslate } = JSON.parse(d.data) as { msgAssistant?: number; msgTranslate?: number };
            if (!d.data) {
                return;
            }
            this.ctx.mq.emit(
                "user_permission",
                { user_permission: { msgAssistant: msgAssistant, msgTranslate: msgTranslate } },
                this.is_full
            );
        } else if (d.resType === "msg_local_delete") {
            const data = JSON.parse(d.data) as { tid: string; mids: string[] };
            const tid = data.tid;
            if (!tid) {
                log.warn(`BUG!!用户对象msg_local_delete的resId${d.resId}解析失败`);
                return;
            }
            for (const mid of data.mids) {
                if (!mid || mid.length === 0) {
                    continue;
                }
                msg_query_one(this.ctx.db, mid).then((res) => {
                    console.log(">>>>>>>>>本地删除消息 111", JSON.stringify(res));
                    if (res.error) {
                        log.error("查询消息" + mid + "失败", res.error);
                        return;
                    }
                    if (!res.msg) {
                        log.info("消息" + mid + "本地不存在，忽略删除");
                        return;
                    }

                    const msg = res.msg;
                    msg.mask = MessageMaskType.DELETED; // 标记为删除
                    msg_update_stat(this.ctx.db, msg, msg.meta).then((r2) => {
                        if (r2.error) {
                            log.error("更新消息" + mid + "状态失败", r2.error, "消息：", msg);
                            return;
                        }
                    })

                    console.log(">>>>>>>>>本地删除消息 222", JSON.stringify(msg));
                    this.ctx.mq.emit("msg_update", { msg_update: { tid, mid } }, this.is_full);
                });
            }
        } else {
            log.warn(`BUG!!未知的用户对象类型${JSON.stringify(d)}`); // 因为绝不可能所以打BUG日志
        }

        return;
    }

    private async do_stat_msg(s: Stat): Promise<undefined> {
        if (!s.data) {
            return;
        }

        const d = fixStatMsg(s.data as StatMsg);
        log.debug("-----------", d.mid, d.mask, s.data);
        let rtc;
        if (config_app_is_miso() && d.rtcId) {
            rtc = await RTCEngine.callRtcQueryStatus(d.rtcId);
        }
        const r = await msg_query_one(this.ctx.db, d.mid);
        if (r.error) {
            log.error("查询消息" + d.mid + "失败");
            return;
        }

        if (!r.msg) {
            log.info("消息" + d.mid + "本地不存在，忽略其状态处理");
            return;
        }
        const tid = r.msg.tid;
        const m: Msg = d as Msg;
        let meta: Meta = {};
        if (
            r.msg &&
            (r.msg.type === MessageType.RTC_VIDEO || r.msg.type === MessageType.RTC_VOICE) &&
            rtc &&
            rtc.data &&
            rtc.data.rtc &&
            rtc.data.rtc.rtcId
        ) {
            meta.rtcId = rtc.data.rtc.rtcId?.toString();
            meta.duration = m.duration || 0;
            meta.status = rtc.data.rtc.status;
            meta.memberList = rtc.data.rtc.memberList;
        }
        m.type = r.msg.type;
        const r2 = await msg_update_stat(this.ctx.db, m, meta);
        if (r2.error) {
            log.error("更新消息" + d.mid + "状态失败", r2.error, "消息：", m);
            return;
        }
        this.ctx.mq.emit("msg_update", { msg_update: { tid: tid, mid: d.mid } }, this.is_full);
    }

    // 处理会话消息
    private async do_task_topic_msgs(): Promise<null> {
        // 当前的会话消息批量落库
        // 记录下变更的会话
        // 都落库完成后，将变更的会话计算一下 新消息 未读数量 at我的数量等界面需要的数据，进行通知
        // this.task_topic_msgs = [];
        const t = this.task_topic_msgs.pop();
        if (!(t && t.msgs && t.msgs.length > 0)) {
            return null;
        }
        const batchid = time.toUnixMillSecond(time.now()); // 批次id
        if (t && t.next) {
            if (t.msgs && t.msgs.length > 0) {
                // 最新消息里面最小的消息id作为查历史的最大消息
                const mn = t.msgs.length;
                const nextMaxMid = t.msgs[mn - 1].mid;

                let tryCnt = 0;
                while (this.is_running() && tryCnt < 3) {
                    tryCnt++;
                    log.info(`${mn}获取会话${t.tid}的历史消息范围[${t.minMsgId},${nextMaxMid})`);
                    // 消息并没有获取完全 获取历史消息
                    const rst = await this.ctx.conn.Call("/v1/sdk/msg/history", {
                        tid: t.tid,
                        minMsgId: t.minMsgId,
                        maxMsgId: nextMaxMid,
                        containMin: true,
                        containMax: false,
                        size: 100,
                    });

                    if (!(rst && rst.code === 0)) {
                        log.warn(`获取会话${t.tid}的历史消息范围[${t.minMsgId},${nextMaxMid}) 失败! 原因`, rst);
                        await time.sleep(2 * time.SECOND);
                        continue;
                    }

                    if (rst.data && rst.data.msgs) {
                        t.msgs.push(...rst.data.msgs);
                    }
                    break;
                }
            }
        }

        {
            const n = t.msgs.length;
            for (let i = 0; i < n; i++) {
                const msg = t.msgs[i];
                msg.content = CryptEngine.decode(msg.encrypt, msg.content, msg.cid);
                msg.tid = t.tid;
                const isMiso = config_app_is_miso();
                if (!isMiso) {
                    continue;
                }
                if (
                    (msg.type === MessageType.RTC_VOICE || msg.type === MessageType.RTC_VIDEO) &&
                    msg.rtcId
                ) {
                    let meta: Meta = {};
                    if (msg.visible === -2) {
                        meta.rtcId = msg.rtcId?.toString();
                        meta.duration = msg.duration || 0;
                    } else {
                        const rtc: any = await RTCEngine.rtcQuery(msg.rtcId.toString());
                        if (rtc && rtc.data && rtc.data.rtc && rtc.data.rtc.rtcId) {
                            meta.rtcId = rtc.data.rtc.rtcId?.toString();
                            meta.duration = msg.duration || 0;
                            meta.status = rtc.data.rtc.status;
                            meta.memberList = rtc.data.rtc.memberList;
                            msg.rtcId = undefined;
                        } else {
                            meta.rtcId = msg.rtcId?.toString();
                            meta.duration = msg.duration || 0;
                        }
                    }
                    msg.meta = meta;
                }
            }
        }

        const dbRst = await msg_batch_add(this.ctx.db, this.ctx.uid, batchid, t.msgs);
        if (dbRst.error) {
            log.warn(`会话${t.tid}的消息落库失败`, dbRst);
        } else {
            const trow = await topic_query_one(this.ctx.db, t.tid);
            if (trow.topic && (trow.topic.msg_synced ?? 0) !== 1) {
                const c = await msg_count(this.ctx.db, t.tid, {
                    expectSender: this.ctx.uid,
                    minMid: trow.topic.lsMid || "",
                    mask: 0,
                });
                const fromDb = c.count ?? 0;
                const prevUnread = Math.max(0, trow.topic.unread ?? 0);
                const merged = Math.max(prevUnread, fromDb);
                if (merged !== prevUnread) {
                    await topic_modify(this.ctx.db, { tid: t.tid, unread: merged });
                }
            }
        }

        if (getTypeByTopicid(t.tid) === "group" && t.msgs.length > 0) {
            const senderUids: number[] = [];
            const uidSet = new Set<number>();
            for (const msg of t.msgs) {
                if (msg.sender && !uidSet.has(msg.sender)) {
                    if (msg.type !== MessageType.SYSTEM || !isSystemAcc(msg.sender)) {
                        uidSet.add(msg.sender);
                        senderUids.push(msg.sender);
                    } else {
                        const datas = msg.content ? JSON.parse(msg.content) : {};
                        const joinUids = datas.joinUids || [];
                        const uid = datas.uid || 0;

                        if (uid && uid !== 0 && !isSystemAcc(uid)) {
                            uidSet.add(uid);
                            senderUids.push(uid);
                        }

                        joinUids.forEach((joinUid: number) => {
                            uidSet.add(joinUid);
                            senderUids.push(joinUid);
                        });
                    }
                }
            }
            if (senderUids.length > 0) {
                const missR = await group_member_missing_uids(this.ctx.db, t.tid, senderUids);
                const needRemote = missR.error != null ? senderUids : missR.missing;
                if (needRemote.length > 0) {
                    try {
                        const r2 = await GroupController.GetUserRemote(this.ctx, t.tid, needRemote);
                        if (r2.data && r2.data.users && r2.data.users.length > 0) {
                            const members: GroupMember[] = [];
                            for (const user of r2.data.users) {
                                const member = user as GroupMember;
                                member.tid = t.tid;
                                members.push(member);
                            }
                            await group_member_upsert(this.ctx.db, members);
                        }
                    } catch (err) {
                        log.warn(`增量消息补全群成员失败 tid=${t.tid}`, err);
                    }
                }
            }
        }

        this.next_acks.push({ tid: t.tid, mid: t.maxMsgId });
        const uid = t.msgs.length > 0 ? t.msgs[0].sender : 0;
        if (t.msgs.length > 0) {
            this.ctx.mq.emit("open_notify", { open_notify: { msg: t.msgs[0] } });
        }
        this.ctx.mq.emit_stash("msg_new", { msg_new: { tid: t.tid, uid: uid } });
        const rtcMsgs = t.msgs.filter(
            (v) => v.type === MessageType.RTC_VOICE || v.type === MessageType.RTC_VIDEO,
        );
        if (rtcMsgs.length > 0) {
            this.ctx.mq.emit_stash("rtc_msg", {
                rtc_msg: { tid: t.tid, uid: uid, msgs: rtcMsgs },
            });
        }

        return null;
    }

    private async do_sync(): Promise<number> {
        if (this.fullSyncRunner.isRunning) {
            log.info("[Sync] 全量同步进行中，跳过 /sync，等待完成");
            return 2 * time.SECOND;
        }

        log.info("[SyncRunable] do_sync /sync", { sid: this.syncIds.sid, aid: this.syncIds.aid });
        const rst = await this.ctx.conn.Call("/sync", {
            sid: this.syncIds.sid,
            aid: this.syncIds.aid,
        });

        if (!(rst && rst.code === 0)) {
            log.warn("sync fail!", rst);
            if (rst && rst.code === ErrorCodePermission) {
                log.info("SYNC会话已经失效 222", rst);

                this.Destory();
                return 0;
            }
            return 3 * time.SECOND;
        }

        if (rst.data) {
            if (!this._hasFullSynced) {
                log.info("[Sync] 首次从未全量同步过，启动全量同步");
                if (rst.data.aid && rst.data.aid > this.syncIds.aid) {
                    this.syncIds.aid = rst.data.aid;
                }
                const serverSid = rst.data.sid ?? 0;
                this.ctx.mq.emit("full_process", {
                    full_process: { name: "begin", process: 0, count: 0 },
                });
                await this.fullSyncRunner.run(serverSid);
                this._hasFullSynced = true;
                await config_set(this.ctx.db, "has_full_synced", "1");
                log.info("[Sync] 首次全量同步完成，标记 has_full_synced=1");

                const reSyncRst = await this.ctx.conn.Call("/sync", {
                    sid: 0,
                    aid: this.syncIds.aid,
                });
                if (reSyncRst && reSyncRst.code === 0 && reSyncRst.data) {
                    const newSid = reSyncRst.data.sid ?? 0;
                    if (newSid > 0) {
                        this.syncIds.sid = newSid;
                        await config_set(this.ctx.db, "sid", this.syncIds.sid.toString(10));
                        this.lastSyncIds.sid = this.syncIds.sid;
                        log.info("[Sync] 全量后 /sync 拿到 sid=", newSid);
                    } else {
                        log.info("[Sync] 全量后 /sync 仍返回 sid=0，等待后续增量推送");
                    }
                    if (reSyncRst.data.aid && reSyncRst.data.aid > this.syncIds.aid) {
                        const stats = await this.stat_pull(this.syncIds.aid + 1, -1);
                        this.syncIds.aid = stats.nextAid;
                        this.push_stats_dedup(stats.stats);
                    }
                    if (reSyncRst.data.sid && reSyncRst.data.sid > this.syncIds.sid) {
                        const minSid = this.syncIds.sid > 0 ? this.syncIds.sid : 1;
                        const nextSid = await this.msg_latest(minSid, reSyncRst.data.sid);
                        this.syncIds.sid =
                            nextSid > this.syncIds.sid ? nextSid : reSyncRst.data.sid;
                        await config_set(this.ctx.db, "sid", this.syncIds.sid.toString(10));
                        this.lastSyncIds.sid = this.syncIds.sid;
                    }
                }
                await config_set(this.ctx.db, "aid", this.syncIds.aid.toString(10));
                this.lastSyncIds.aid = this.syncIds.aid;
                return 80 * time.MILLISECOND;
            }

            if (rst.data.aid && rst.data.aid > this.syncIds.aid) {
                const stats = await this.stat_pull(this.syncIds.aid + 1, -1);
                log.info(
                    "=====sync返回的aid为",
                    rst.data.aid,
                    "pull最终的aid为",
                    stats.nextAid,
                );
                this.syncIds.aid = stats.nextAid;
                this.push_stats_dedup(stats.stats);
            }

            if (rst.data.sid && rst.data.sid > this.syncIds.sid) {
                const minSid = this.syncIds.sid > 0 ? this.syncIds.sid : 1;
                let nextSid = await this.msg_latest(minSid, rst.data.sid);
                if (nextSid <= this.syncIds.sid) {
                    nextSid = rst.data.sid;
                }
                log.info("sync返回的sid为", rst.data.sid, "msgpull最终的sid为", nextSid);
                this.syncIds.sid = nextSid;
            }

            const taskCount = this.calcTaskCount();
            if (taskCount >= FULL_TASK_THRESHOLD) {
                log.info("[Sync] 任务积压", taskCount, "条，清空增量队列，启动全量同步");
                this.task_topic_msgs = [];
                this.task_stats = [];
                const serverSid = rst.data.sid ?? this.syncIds.sid;
                this.ctx.mq.emit("full_process", {
                    full_process: { name: "begin", process: 0, count: taskCount },
                });
                const fullRst = await this.fullSyncRunner.run(serverSid);
                if (fullRst.sid > 0) {
                    this.syncIds.sid = fullRst.sid;
                    await config_set(this.ctx.db, "sid", this.syncIds.sid.toString(10));
                    this.lastSyncIds.sid = this.syncIds.sid;
                }
                if (!this._hasFullSynced) {
                    this._hasFullSynced = true;
                    await config_set(this.ctx.db, "has_full_synced", "1");
                }
                const reSyncRst = await this.ctx.conn.Call("/sync", {
                    sid: this.syncIds.sid,
                    aid: this.syncIds.aid,
                });
                if (reSyncRst && reSyncRst.code === 0 && reSyncRst.data) {
                    const newSid = reSyncRst.data.sid ?? 0;
                    if (newSid > this.syncIds.sid) {
                        this.syncIds.sid = newSid;
                        await config_set(this.ctx.db, "sid", this.syncIds.sid.toString(10));
                        this.lastSyncIds.sid = this.syncIds.sid;
                        log.info("[Sync] 全量后 /sync 拿到 sid=", newSid);
                    }
                    if (reSyncRst.data.aid && reSyncRst.data.aid > this.syncIds.aid) {
                        const stats = await this.stat_pull(this.syncIds.aid + 1, -1);
                        this.syncIds.aid = stats.nextAid;
                        this.push_stats_dedup(stats.stats);
                    }
                    if (reSyncRst.data.sid && reSyncRst.data.sid > this.syncIds.sid) {
                        const minSid2 = this.syncIds.sid > 0 ? this.syncIds.sid : 1;
                        const nextSid2 = await this.msg_latest(minSid2, reSyncRst.data.sid);
                        this.syncIds.sid =
                            nextSid2 > this.syncIds.sid ? nextSid2 : reSyncRst.data.sid;
                        await config_set(this.ctx.db, "sid", this.syncIds.sid.toString(10));
                        this.lastSyncIds.sid = this.syncIds.sid;
                    }
                }
                await config_set(this.ctx.db, "aid", this.syncIds.aid.toString(10));
                this.lastSyncIds.aid = this.syncIds.aid;
                return 80 * time.MILLISECOND;
            }
        }

        return 80 * time.MILLISECOND;
    }

    // 拉取最新的消息
    // 后台发现sid为0的时候，只返回一页，不给第二页
    // 本地为空 就拿最大的修正即可
    private async msg_latest(minSyncId: number, maxSyncId: number): Promise<number> {
        let nextSid = maxSyncId > minSyncId ? maxSyncId : minSyncId;
        let reqCnt = 0;
        let topicsCnt = 0;
        while (this.is_running()) {
            log.info(`准备拉取的消息范围[${minSyncId},${maxSyncId}]`);
            const acks: Ack[] = [...this.next_acks];
            this.next_acks = [];
            // 会话还有消息 继续拉取 直到最后位置
            const rst = await this.ctx.conn.Call("/v1/sdk/msg/latest", {
                ack: acks,
                sid: minSyncId,
                maxSid: maxSyncId,
            });

            if (!(rst.code === 0)) {
                this.next_acks.push(...acks); // ack没有正确上报重新进入队列
                log.error("拉取最新消息异常 err:", rst);
                // todo 这里通过监听报告给外部进行错误提示
                await time.sleep(3 * time.SECOND);
                continue;
            }

            reqCnt++;
            const data = rst.data;
            // 会话没有任何新消息 topics 不会返回
            if (data.topics) {
                topicsCnt += data.topics.length;
                log.info(`消息同步返回会话数量${data.topics.length}`);

                this.push_topic_msgs_dedup(data.topics);
            } else {
                log.info(`消息同步返回会话数量0`);
            }

            if (data.nsid) {
                nextSid = data.nsid > nextSid ? data.nsid : nextSid;
            }

            if (data.leftReq) {
                // 有剩余会话
                log.info(`消息同步[${minSyncId},${maxSyncId}]一次没有拉取完，需要分页继续[${data.leftReq.sid},${data.leftReq.maxSid}]`);
                minSyncId = data.leftReq.sid;
                maxSyncId = data.leftReq.maxSid;
            } else {
                break;
            }
        }

        log.info(`消息同步完成，总共用请求了${reqCnt}次,总的会话数量${topicsCnt}个`);
        return nextSid;
        // 修正下一次sync的游标
        // 如果返回的数据中最下的syncid比当前的都还要大，说明没有拉取完全。
        // 本地的一些会话
        // 消息在本地存好
        // 定位到 某条消息 然后获取以及上下多少条
    }

    // 拉取最新的状态
    private async stat_pull(minStatId: number, maxStatId: number): Promise<{ nextAid: number; stats: Stat[] }> {
        let nextAid = maxStatId > minStatId ? maxStatId : minStatId;
        const stats: Stat[] = [];
        while (this.is_running()) {
            const rst = await this.ctx.conn.Call<MsgLatest>("/v1/sdk/stat/pull", {
                minStatId,
                maxStatId,
            });
            if (rst.code === 0) {
                // 成功
            } else {
                // todo 这里通过监听报告给外部进行错误提示
                log.error("/stat/pull err:", rst);
                await time.sleep(3 * time.SECOND);
                continue;
            }

            if (rst.data.stats && rst.data.stats.length > 0) {
                const n = rst.data.stats.length;
                for (let i = 0; i < n; i++) {
                    const a = rst.data.stats[i] as Stat;
                    stats.push(a);
                }
            }

            if (rst.data.maxStatId && nextAid < rst.data.maxStatId) {
                log.info("pull返回的最大aid为", rst.data.maxStatId, "比nextAid", nextAid, "大，修正nextAid");
                nextAid = rst.data.maxStatId;
            } else {
                log.info("pull返回的最大aid为", rst.data.maxStatId, "比nextAid", nextAid, "小");
            }

            if (rst.data.leftReq) {
                // 还有任务
                minStatId = rst.data.leftReq.minStatId;
                maxStatId = rst.data.leftReq.maxStatId;
            } else {
                break;
            }
        }
        return { nextAid, stats };
    }
}

// 会话额外补充几个信息
// 上一次同步id 上一次同步时间 上一次拉取消息时间 上一次拉取消息id

// 下一次获取
type MsgLatestLeft = {
    sid: number; // 最小的sid 为了向下兼容，在取消息的时候会+1相当于是左边是开区间
    maxSid: number; // 0909新增 为了向下兼容，这个值小于等于0的时候相当于取无穷大。 只有大于0的时候才有效
};

// 响应:会话消息部分
type MsgLatestTopic = {
    tid: string; // 会话id
    sid: number; // 最近的值越大 越是最近更新
    minMsgId: string; // 后台查询的消息范围:最小的消息id
    maxMsgId: string; // 后台查询的消息范围:最大的消息id
    next: boolean; // 是否一次性把离线拉取完了 如果有剩余 Next = true
    msgs: Msg[]; // 消息内容
};

// 响应body
type MsgLatestRsp = {
    nsid: number; // 目前最大的sid 后面去sync就用这个值
    topics?: MsgLatestTopic[]; // 返回有消息的会话
    leftReq?: MsgLatestLeft; // 0909新增 一次性没拉取完成，分多次继续请求
};
// 响应
type MsgLatest = {
    code: number;
    msg: string;
    data?: MsgLatestRsp;
};

// 状态同步：消息
type StatMsg = {
    mid: string; // 由 topicid:tmid 拼接而成 保证唯一
    tid?: string; // TopidId：g_topicid s_sys_userid 通过topicid的前缀 g_ s_ 区分单聊和群聊 系统给用户自动创建的会话;单聊是两个用户id排序后组合；
    sender?: number; // 发送方
    targetCnt?: number; // 目标用户数
    targetUids?: number[]; // 目标用户列表 []int64
    readCnt?: number; // 已读的数量
    readUids?: number[]; //  []int64  已读用户列表
    atCnt?: number; // 这条消息被@的人数量
    atUids?: number[]; //  []int64 这条消息被@的人列表
    atReadCnt?: number; // 这条消息被@的人已读数量
    atReadUids?: number[]; // []int64   这条消息被@的人已读列表
    mask?: number; // 0-正常 1-删除 2-撤回
    visible?: number; // 0-正常所有人可见 1-发送者和群管理可见
    auditRst?: string; // 空-无操作 pass - 允许 refused - 拒绝 json序列化会自动去掉为空的字段
    rtcId?: any;
};

function fixStatMsg(s: StatMsg): StatMsg {
    return Object.assign({}, defaultStatMsg, s);
}

// 防止空字段
const defaultStatMsg: StatMsg = {
    mid: "", // 由 topicid:tmid 拼接而成 保证唯一
    tid: "", // TopidId：g_topicid s_sys_userid 通过topicid的前缀 g_ s_ 区分单聊和群聊 系统给用户自动创建的会话;单聊是两个用户id排序后组合；
    sender: 0, // 发送方
    targetCnt: 0, // 目标用户数
    targetUids: [], // 目标用户列表 []int64
    readCnt: 0, // 已读的数量
    readUids: [], //  []int64  已读用户列表
    atCnt: 0, // 这条消息被@的人数量
    atUids: [], //  []int64 这条消息被@的人列表
    atReadCnt: 0, // 这条消息被@的人已读数量
    atReadUids: [], // []int64   这条消息被@的人已读列表
    mask: 0, // 0-正常 1-删除 2-撤回
    visible: 0, // 0-正常所有人可见 1-发送者和群管理可见
    auditRst: "", // 空-无操作 pass - 允许 refused - 拒绝 json序列化会自动去掉为空的字段
};
